import { Component, OnInit, Input, Output, EventEmitter, signal } from '@angular/core';
import { trigger, transition, animate, keyframes, style } from '@angular/animations';

import { Notification } from '../../../../interfaces/notification.model';
import { Router } from '@angular/router';
import {
  ColDef,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  GridState,
  RowClickedEvent,
  RowDataUpdatedEvent
} from 'ag-grid-community';
import {
    COL_DEF_NOTIFICATION_NAME,
    COL_DEF_DYNAMIC_NOTIFICATION,
    COL_DEF_READ_STATE,
    COL_DEF_ACTIVITY
} from '../notifications-grid/notification-column-defs';

import {
  notificationsGridConfig,
  notificationsGridDefaultColDef,
  sizeColsToFit
} from '../notifications-grid/notification-helpers';
import { Store } from '@ngxs/store';
import { PutNotificationRead } from '@app/app/store/notifications/notifications.actions';

@Component({
  selector: 'app-notification-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss', '../../styles.temp.scss'],
  animations: [
    trigger('fadeInOutFloat', [
      transition(':enter', [
        animate('0.25s', keyframes([
          style({ position: 'absolute', 'z-index': -1, width: '100%', opacity: 0, offset: 0 }),
          style({ opacity: 0.667, offset: 0.5 }),
          style({ opacity: 1, offset: 0.75 }),
          style({ offset: 1 })
        ])),
      ]),
      transition(':leave', [
        animate('0.25s', keyframes([
          style({ position: 'absolute', 'z-index': -1, width: '100%', opacity: 1, offset: 0 }),
          style({ opacity: 0, offset: 1 }),
        ])),
      ])
    ]),
    trigger('fadeSlideInOut', [
      transition(':enter', [
        animate('0.25s', keyframes([
          style({ transform: 'translateX(100%)', opacity: 0, offset: 0 }),
          style({ transform: 'translateX(50%)', opacity: 0.667, offset: 0.5 }),
          style({ transform: 'translateX(25%)', opacity: 1, offset: 0.75 }),
          style({ transform: 'translateX(0%)', offset: 1 })
        ])),
      ]),
      transition(':leave', [
        animate('0.25s', keyframes([
          style({ transform: 'translateX(0%)', opacity: 1, offset: 0 }),
          style({ transform: 'translateX(100%)', opacity: 0, offset: 1 }),
        ])),
      ])
    ])
  ],
  standalone: false
})
export class SidenavComponent implements OnInit {
    @Input() notifications: Notification[] | null;
    @Input() allRead = false;
    @Input() loading = true;
    @Input() togglingAllRead: boolean;
    @Input() deletingAll: boolean;

    @Output() toggle = new EventEmitter();
    @Output() bulkRead = new EventEmitter();
    @Output() bulkDelete = new EventEmitter();
    public noRows = signal(true);
    public sortDirection: 'asc' | 'desc' = 'desc';
    public isSorted: boolean = false;
    protected readonly notificationsGridConfig = notificationsGridConfig;
    public  notificationsGridInitialState: GridState;
    protected readonly colDefDefault = notificationsGridDefaultColDef;
    private _gridApi: GridApi | undefined;
    public colDefReadState: ColDef = COL_DEF_READ_STATE;
      public colDefNotificationName: ColDef = COL_DEF_NOTIFICATION_NAME;
      public colDefDynamicNotification: ColDef = COL_DEF_DYNAMIC_NOTIFICATION;
      public colDefAssigned: ColDef = COL_DEF_ACTIVITY;
      public quickFilterValue = '';
      public selectedRowNodeId: string;
      public notificationsGridOptions: GridOptions = Object.assign({}, {
        ...this.notificationsGridConfig,
        defaultColDef: this.colDefDefault,
        columnDefs: [
          this.colDefReadState,
          this.colDefNotificationName,
          this.colDefAssigned,
          this.colDefDynamicNotification,
        ]
      });

    constructor(public router: Router, private store: Store) { 

    }

    ngOnInit(): void {
    }

    toggleOpen() {
        this.toggle.emit();
    }

    deleteAll() {
        const activityIds = this.getFilteredRowIds();
        this.bulkDelete.emit(activityIds);
    }

    markAllRead() {
        const activityIds = this.getFilteredRowIds();
        this.bulkRead.emit(activityIds);
    }

    trackByFn(index: any, notification: { activityId: any; }) {
        return notification.activityId;
    }

    onGridReady(event: GridReadyEvent): void {
      this._gridApi = event.api;
      this._gridApi?.applyColumnState({
        state: [ {colId: 'created', sort: this.sortDirection }]
      });
    }

    onQuickFilterChange(value): void {
      this._gridApi?.setGridOption('quickFilterText', value);
    }

    onRowClicked(event: RowClickedEvent): void {
      const { data, node } = event;
      if (!data.read) {
        this.store.dispatch(new PutNotificationRead(data.activityId));
      }
      this.selectedRowNodeId = node.data.id;

      if (data.portalUrl) {
        this.router.navigateByUrl(data.portalUrl);
      } else {
        this.router.navigateByUrl(`/deal/${data.dealId}`);
      }
    }

    onFirstDataRendered(event: FirstDataRenderedEvent): void {
        setTimeout(() => sizeColsToFit(event.api), 500);
    }

    onRowDataUpdated(event: RowDataUpdatedEvent) {
      this._gridApi?.refreshCells();
      if (this._gridApi?.getDisplayedRowCount() === 0) {
        this.noRows.set(true);
      } else {
        this.noRows.set(false);
      }
    }

    onFilterChanged(event: FilterChangedEvent): void {
      event.api.deselectAll();
    }

    handleSortDirection(order: 'asc' | 'desc') {
      this._gridApi?.applyColumnState({
        state: [ {colId: 'created', sort: order }]
      });
  
      this.isSorted = true;
      this.sortDirection = order;
    }

    getFilteredRows(): any[] {
      const filteredRows: any[] = [];
      this._gridApi?.forEachNodeAfterFilter((node) => {
        if (node.data) {
          filteredRows.push(node.data);
        }
      });
      return filteredRows;
    }

    getFilteredRowIds(): string[] {
      return this.getFilteredRows().map(row => row.activityId);
    }
}
